/**
 * @flow
 */

import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Form, Button } from 'semantic-ui-react';
import { FormLabel } from '../../../../components';
import { defineMessages, injectIntl } from 'react-intl';
import { some, find, reduce, filter, map } from 'lodash';
import { format, addMinutes, startOfDay } from 'date-fns';

import {
    MasterSelect,
    TimeSelect,
    CabinetSelect,
} from '../../containers/form';
import ServiceSelectContainer from '../../containers/form/ServiceSelect';
import ServiceDurationSelect from '../../../settings/containers/service/service/ServiceDurationSelect';

import styles from '../../../../styles';
import { getFreeTimeSpans, getInvertedTimeSpans } from '../../../../lib/schedule';
import { sortByName } from '../../../../lib/servicesSort';

// flow types
import type { Intl } from '../../../../type';

const intlMessages = {
    cabinet: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.cabinetPlaceholder',
            defaultMessage: 'Cabinet',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.selectCabinetLabel',
            defaultMessage: 'Cabinet',
        },
    }),
    master: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.masterPlaceholder',
            defaultMessage: 'Master',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.selectMasterLabel',
            defaultMessage: 'Master',
        },
    }),
    service: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.servicePlaceholder',
            defaultMessage: 'Service',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.serviceLabel',
            defaultMessage: 'Service',
        },
    }),
    startTime: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.startTimePlaceholder',
            defaultMessage: 'Time',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.timeLabel',
            defaultMessage: 'Time',
        },
    }),
    duration: defineMessages({
        placeholder: {
            id: 'pages.appointments.form.ServiceFields.durationPlaceholder',
            defaultMessage: 'Duration',
        },
        label: {
            id: 'pages.schedule.form.AppointmentForm.durationLabel',
            defaultMessage: 'Duration',
        },
    }),
};

type ServiceFieldsProps = {
    index: number,
    onRemove: Function,
    readOnly: boolean,
    intl: Intl,
    cabinets: Array<Object>,
    loading: boolean,
    withLabel: boolean,
    disableRemove: boolean,
};

const RemoveButton = styled(Button)`
    margin-right: 0px !important;
`;

const FormGroup = styled(Form.Group)`
    align-items: flex-start;
`;

const Label = styled(FormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

function CabinetServiceFields({
    index,
    onRemove,
    intl,
    cabinets,
    withLabel,
    disableRemove,
}: ServiceFieldsProps) {
    const { control, setValue } = useFormContext();
    const fieldPath = index !== undefined ? `services.${index}` : '';

    const cabinetId = useWatch({ control, name: fieldPath ? `${fieldPath}.cabinet` : 'cabinet' });
    const serviceId = useWatch({ control, name: fieldPath ? `${fieldPath}.service` : 'service' });
    const masterId = useWatch({ control, name: fieldPath ? `${fieldPath}.master` : 'master' });
    const duration = useWatch({ control, name: fieldPath ? `${fieldPath}.duration` : 'duration' });

    const settings = useSelector(({ salon }) => salon.get('settings'));

    const { services, masters, timeOptions } = useMemo(() => {
        const cabinet = find(cabinets, c => c.id === cabinetId);

        const svcList = cabinet
            ? reduce(cabinet.serviceGroups, (res, { services: s }) => [...res, ...s], [])
            : [];

        const serviceGroup =
            cabinet &&
            find(cabinet.serviceGroups, sg =>
                some(sg.services, s => s.id === serviceId),
            );

        const masterList = serviceGroup
            ? filter(serviceGroup.masters, ({ schedule }) => schedule && schedule.length)
            : [];

        let tOptions = [];
        if (cabinetId && serviceId && cabinet) {
            if (!svcList.length || !some(svcList, ['id', serviceId])) {
                setTimeout(() => {
                    setValue(fieldPath ? `${fieldPath}.service` : 'service', null);
                }, 0);
            }

            if (masterId && (!masterList.length || !some(masterList, ['id', masterId]))) {
                setTimeout(() => {
                    setValue(fieldPath ? `${fieldPath}.master` : 'master', null);
                }, 0);
            }

            const notWorkingTime = getInvertedTimeSpans({
                startAt: settings.startAt,
                endAt: settings.endAt,
                timeSpans: map(cabinet.schedule, wt => [wt.startAt, wt.endAt]),
            });

            if (masterId && serviceGroup) {
                const master = find(serviceGroup.masters, ({ id }) => id === masterId);
                if (master) {
                    const mastersNotWorkingTime = getInvertedTimeSpans({
                        startAt: settings.startAt,
                        endAt: settings.endAt,
                        timeSpans: map(master.schedule, wt => [wt.startAt, wt.endAt]),
                    });
                    notWorkingTime.push(...mastersNotWorkingTime);
                }
            }

            const freeTimeSpans = getFreeTimeSpans({
                timeSpan: [settings.startAt, settings.endAt],
                duration,
                exclude: notWorkingTime,
                step: settings.step,
            });

            tOptions = map(freeTimeSpans, timeSpan => ({
                text: format(
                    addMinutes(startOfDay(new Date()), timeSpan[0]),
                    settings.format || 'HH:mm',
                ),
                value: timeSpan[0],
            }));
        }

        return {
            services: sortByName(svcList),
            masters: masterList,
            timeOptions: tOptions,
        };
    }, [cabinets, cabinetId, serviceId, masterId, duration, settings, fieldPath, setValue]);

    return (
        <FormGroup>
            <Controller
                name={fieldPath ? `${fieldPath}.cabinet` : 'cabinet'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={4} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.cabinet.label)}
                            </Label>
                        )}
                        <CabinetSelect
                            value={value}
                            onChange={(e, { value: v }) => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.cabinet.placeholder)}
                            fluid
                            search
                            cabinets={cabinets}
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.service` : 'service'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={4} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.service.label)}
                            </Label>
                        )}
                        <ServiceSelectContainer
                            value={value}
                            onChange={v => onChange(v)}
                            field={fieldPath}
                            change={(name, val) => setValue(name, val)}
                            placeholder={intl.formatMessage(intlMessages.service.placeholder)}
                            fluid
                            search
                            services={services}
                            disabled={!cabinetId}
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.master` : 'master'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={3} error={!!fieldState.error}>
                        {withLabel && (
                            <Label>
                                {intl.formatMessage(intlMessages.master.label)}
                            </Label>
                        )}
                        <MasterSelect
                            value={value}
                            onChange={v => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.master.placeholder)}
                            fluid
                            search
                            masters={masters}
                            disabled={!serviceId}
                            clearable
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.duration` : 'duration'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={3} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.duration.label)}
                            </Label>
                        )}
                        <ServiceDurationSelect
                            value={value}
                            onChange={(e, { value: v }) => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.duration.placeholder)}
                            fluid
                            disabled={!serviceId}
                        />
                    </Form.Field>
                )}
            />
            <Controller
                name={fieldPath ? `${fieldPath}.startTime` : 'startTime'}
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                    <Form.Field width={2} error={!!fieldState.error}>
                        {withLabel && (
                            <Label className="required">
                                {intl.formatMessage(intlMessages.startTime.label)}
                            </Label>
                        )}
                        <TimeSelect
                            value={value}
                            onChange={(e, { value: v }) => onChange(v)}
                            placeholder={intl.formatMessage(intlMessages.startTime.placeholder)}
                            fluid
                            options={timeOptions}
                            disabled={!serviceId}
                        />
                    </Form.Field>
                )}
            />
            {index !== undefined && (
                <Form.Field>
                    <RemoveButton
                        onClick={onRemove}
                        color="red"
                        icon="trash"
                        disabled={disableRemove}
                        basic
                    />
                </Form.Field>
            )}
        </FormGroup>
    );
}

export default injectIntl(CabinetServiceFields);
